export type ActiveCellController = {
  activate: () => boolean;
  submit: (options?: { preserveSizeLock?: boolean }) => boolean;
  cancel: (options?: { preserveSizeLock?: boolean }) => void;
  releaseSizeLock: () => void;
};

type OpenCellOptions = { discardActiveCell?: boolean };

type OpenCellResult = 'opened' | 'missing' | 'blocked';

export type CellEditingStore = {
  getActiveCellKey: () => string | null;
  requestOpenCell: (cellKey: string, options?: OpenCellOptions) => void;
  requestPendingOpenCell: (cellKey: string, options?: OpenCellOptions) => void;
  clearPendingCell: () => void;
  clearActiveCell: (cellKey: string) => void;
  restoreFailedCell: (cellKey: string) => boolean;
  registerCellController: (cellKey: string, controller: ActiveCellController | null) => void;
  submitActiveCell: () => boolean;
  cancelActiveCell: () => void;
};

export function createCellEditingStore(): CellEditingStore {
  let activeCellKey: string | null = null;
  let pendingCellKey: string | null = null;
  const cellControllers = new Map<string, ActiveCellController>();

  function openCell(cellKey: string, options?: OpenCellOptions): OpenCellResult {
    if (activeCellKey === cellKey) {
      pendingCellKey = null;
      return 'opened';
    }

    const nextCellController = cellControllers.get(cellKey);
    if (!nextCellController) {
      return 'missing';
    }

    const currentActiveCellController = activeCellKey
      ? cellControllers.get(activeCellKey)
      : undefined;

    if (currentActiveCellController) {
      if (options?.discardActiveCell) {
        currentActiveCellController.cancel({ preserveSizeLock: true });
      } else if (!currentActiveCellController.submit({ preserveSizeLock: true })) {
        return 'blocked';
      }
    }

    const activateResult = nextCellController.activate();
    if (activateResult) {
      activeCellKey = cellKey;
      pendingCellKey = null;
      currentActiveCellController?.releaseSizeLock();
      return 'opened';
    }

    if (options?.discardActiveCell) {
      activeCellKey = null;
    }
    currentActiveCellController?.releaseSizeLock();
    return 'blocked';
  }

  return {
    getActiveCellKey() {
      return activeCellKey;
    },
    requestOpenCell(cellKey, options) {
      void openCell(cellKey, options);
    },
    requestPendingOpenCell(cellKey, options) {
      const result = openCell(cellKey, options);
      // Only missing cells become pending; blocked opens should not retry after validation fails.
      if (result === 'missing') {
        pendingCellKey = cellKey;
      }
    },
    clearPendingCell() {
      pendingCellKey = null;
    },
    clearActiveCell(cellKey) {
      if (activeCellKey !== cellKey) {
        return;
      }

      activeCellKey = null;
    },
    restoreFailedCell(cellKey) {
      if (activeCellKey) {
        return false;
      }

      pendingCellKey = null;
      activeCellKey = cellKey;
      return true;
    },
    registerCellController(cellKey, controller) {
      if (!controller) {
        cellControllers.delete(cellKey);

        if (activeCellKey === cellKey) {
          activeCellKey = null;
        }

        return;
      }

      cellControllers.set(cellKey, controller);

      if (pendingCellKey === cellKey) {
        void openCell(cellKey);
      }
    },
    submitActiveCell() {
      const cellKey = activeCellKey;
      if (!cellKey) return true;

      const controller = cellControllers.get(cellKey);
      if (!controller) {
        activeCellKey = null;
        return true;
      }

      return controller.submit();
    },
    cancelActiveCell() {
      const cellKey = activeCellKey;
      if (!cellKey) return;

      activeCellKey = null;
      cellControllers.get(cellKey)?.cancel();
    },
  };
}
